import {Bindings} from "data/constants/bindings";
import {RoundStatus} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {IPlayer, type IPlayersStore} from "data/stores/players/players.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {uniq} from "lodash";
import {makeAutoObservable, observable} from "mobx";

export interface IPlayerInfoProps {
	playerIds?: number[];
	roundId?: number | null;
	singlePlayer?: boolean;
}

export interface IPlayerInfoController extends ViewController<IPlayerInfoProps> {
	get i18n(): ILocalizationStore;
	get players(): (IPlayer | undefined)[];
	get notPlaying(): boolean;

	setPlayerIds: (playerIds: number[]) => void;
	setRoundId: (roundId: number | null) => void;
	setSinglePlayer: (singlePlayer: boolean) => void;
}

@injectable()
export class PlayerInfoController implements IPlayerInfoController {
	@observable private _playerIds = [] as number[];
	@observable _roundId?: number | null;
	@observable _singlePlayer?: boolean = false;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get players() {
		const playerIds = this._singlePlayer
			? [this._playerIds[0]]
			: uniq([this._playerIds[0], ...this.playerIdsPairs]);
		return playerIds.map((id) => this._playerStore.getPlayerById(id));
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get golfers() {
		return this.round?.golfers || [];
	}

	get golfersPairs() {
		return this.golfers.reduce((acc, el) => {
			acc.push([...(el.usa ?? [])]);
			acc.push([...(el.europe ?? [])]);
			return acc;
		}, [] as number[][]);
	}

	get playerIdsPairs() {
		return this.golfersPairs.find((el) => el.includes(this._playerIds[0])) || [];
	}

	get isScheduledSession() {
		return this.round?.status === RoundStatus.Scheduled;
	}

	get notPlaying() {
		return (
			this.golfers.length > 0 && this.playerIdsPairs.length === 0 && !this.isScheduledSession
		);
	}

	setPlayerIds = (playerIds: number[]) => {
		this._playerIds = playerIds;
	};

	setRoundId = (roundId: number | null) => {
		this._roundId = roundId;
	};

	setSinglePlayer = (singlePlayer: boolean) => {
		this._singlePlayer = singlePlayer;
	};

	init({playerIds, roundId, singlePlayer}: IPlayerInfoProps) {
		this._roundId = roundId;
		this._playerIds = playerIds || [];
		this._singlePlayer = singlePlayer;
	}
}
